import * as THREE from "three";
import AppData from "../../../app.data";
import { Loader } from "../../assets/loader";
import Renderer from "../../../renderer/renderer";
export { getSpaceMoodAssetUrls } from "../space.runtime.assets";

const appData = new AppData();

export class SpaceAssetManager {
    clonesCount: number = 0;

    constructor(
        private loader: Loader,
        private renderer: Renderer
    ) { }

    private loadOptionalTexture(url: string, label: string): Promise<any> {
        if (!url) return Promise.resolve(null);
        return this.loader.loadTexture(url).catch((error) => {
            console.warn(`SpaceAssetManager: Failed to load optional texture for ${label}`, { url, error });
            return null;
        });
    }

    private loadOptionalHDR(url: string): Promise<any> {
        if (!url) return Promise.resolve(null);
        return this.loader.loadHDR(url, this.renderer.renderer).catch((error) => {
            console.warn("SpaceAssetManager: Failed to load optional HDR", { url, error });
            return null;
        });
    }

    private loadOptionalClone(url: string): Promise<any> {
        if (!url) return Promise.resolve(null);
        return this.loader.load3DModel(url, false).catch((error) => {
            console.warn("SpaceAssetManager: Failed to load optional clone model", { url, error });
            return null;
        });
    }

    public assembleLoadingPromises(mood: any): Promise<any>[] {
        let modelUrl = appData.spaceData.data.modelUrl;
        let hdriUrl = mood.sky.hdriUrl;
        let skyUrl = mood.sky.skyImageUrl;
        let lightMapUrl = mood.lightMap.lightMapUrl;
        const moodClones = appData.spaceData?.data?.clones || [];
        const moodMaterialOverrides = mood?.materialOverrides || [];

        const clonePromises = moodClones.map((clone: any) => {
            return this.loadOptionalClone(clone.modelUrl ? `${clone.modelUrl}` : "");
        });

        const materialPromises = moodMaterialOverrides.map((override: any, index: number) => {
            return this.loadOptionalTexture(override.textureUrl, `materialOverrides[${index}]`);
        });

        // Store the count for later use
        this.clonesCount = clonePromises.length;

        return [
            modelUrl
                ? this.loader.load3DModel(`${modelUrl}`, false)
                : Promise.resolve(new THREE.Group()),
            this.loadOptionalHDR(hdriUrl),
            this.loadOptionalTexture(skyUrl, "sky"),
            this.loadOptionalTexture(lightMapUrl, "lightMap"),
            ...clonePromises,
            ...materialPromises
        ];
    }

    public handleClone(clones: any[]) {
        if (clones.length > 0) {
            for (let i = 0; i < clones.length; i++) {
                for (let j = 0; j < appData.spaceData.data.clones[i].transforms.length; j++) {
                    if (!clones[i]) continue;
                    let object = clones[i].clone();
                    const cloneData = appData.spaceData.data.clones[i].transforms;
                    object.position.set(cloneData[j].position.x, cloneData[j].position.y, cloneData[j].position.z);
                    object.rotation.set(cloneData[j].rotation.x, cloneData[j].rotation.y, cloneData[j].rotation.z);
                    object.scale.set(cloneData[j].scale.x, cloneData[j].scale.y, cloneData[j].scale.z);
                    appData.spaceScene.add(object);
                }
            }
        }
    }

    public overrideMaterials(mood: any, materialOverrides: any[]) {
        const moodMaterialOverrides = mood?.materialOverrides || [];
        if (moodMaterialOverrides.length > 0) {
            for (let i = 0; i < moodMaterialOverrides.length; i++) {
                const override = moodMaterialOverrides[i];
                const meshObject = appData.spaceScene.getObjectByName(override.meshName);
                if (meshObject instanceof THREE.Mesh) {
                    const texture = materialOverrides[i];
                    if (!texture) {
                        console.warn("SpaceAssetManager: Skipping material override because texture failed to load", {
                            meshName: override.meshName,
                            textureUrl: override.textureUrl
                        });
                        continue;
                    }
                    if (!meshObject.material) continue;
                    texture.wrapS = THREE.RepeatWrapping;
                    texture.wrapT = THREE.RepeatWrapping;
                    let repeat = override.repeat ? override.repeat : 1;
                    texture.repeat.set(repeat, repeat);

                    const channel = String(override.channel || "");
                    if (!channel) continue;

                    const applyOverrideOnMaterial = (material: THREE.Material) => {
                        (material as any)[channel] = texture;
                        if ((material as any)[channel]) {
                            (material as any)[channel].needsUpdate = true;
                        }
                        material.needsUpdate = true;
                    };

                    if (Array.isArray(meshObject.material)) {
                        meshObject.material.forEach((material) => applyOverrideOnMaterial(material));
                    } else {
                        applyOverrideOnMaterial(meshObject.material as THREE.Material);
                    }
                }
            }
        }
    }

    public handleProps(props: any[]) {
        if (props.length > 0) {
            for (let i = 0; i < props.length; i++) {
                const prop = props[i];
                for (let j = 0; j < prop.meshNames.length; j++) {
                    const meshName = prop.meshNames[j];
                    const meshObject = appData.spaceScene.getObjectByName(meshName);
                    if (meshObject instanceof THREE.Mesh) {
                        meshObject.visible = prop.visible;
                    }
                }
            }
        }
    }
}

